import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Activity, ExternalLink, HeartPulse } from "lucide-react";
import { api } from "../../lib/api";

const mxn = (v) => Number(v || 0).toLocaleString("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });
const NIVEL_COLOR = { Excelente: "text-emerald-600", Saludable: "text-emerald-500", "Atención": "text-amber-500", Riesgo: "text-orange-500", "Crítico": "text-destructive", "Sin datos": "text-navy/40" };

function useAccounts() {
  const [accts, setAccts] = useState([]);
  useEffect(() => { api.get("/mod/accounts/items").then((r) => setAccts(r.data.map((a) => a.nombre).filter(Boolean))).catch(() => {}); }, []);
  return accts;
}

export function AccountPicker({ value, onChange, testid }) {
  const accounts = useAccounts();
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full sm:w-80 bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet text-sm">
      <option value="">Selecciona un cliente…</option>
      {accounts.map((a) => <option key={a} value={a}>{a}</option>)}
    </select>
  );
}

const Section = ({ title, items, render }) => items.length > 0 && (
  <div className="card-surface p-4">
    <div className="overline text-navy/40 mb-2">{title} ({items.length})</div>
    <div className="space-y-1.5">{items.slice(0, 8).map(render)}</div>
  </div>
);

export default function Customer360() {
  const [cuenta, setCuenta] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!cuenta) { setData(null); return; }
    setLoading(true);
    api.get(`/mod/cx/360`, { params: { cuenta } }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [cuenta]);

  const row = (mk) => (x) => (
    <Link key={x.id} to={`/os/mod/${mk}`} className="flex items-center justify-between gap-2 border border-navy/10 rounded-lg px-3 py-1.5 hover:border-violet text-sm">
      <span className="truncate text-navy/80">{x.nombre || x.titulo || x.asunto || x.comentario || x.tipo || "—"}</span>
      <ExternalLink className="w-3.5 h-3.5 text-navy/25 shrink-0" />
    </Link>
  );

  return (
    <div className="space-y-4" data-testid="module-customer360">
      <AccountPicker value={cuenta} onChange={setCuenta} testid="c360-account" />
      {loading && <div className="py-8 flex justify-center text-navy/40"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      {!loading && !cuenta && <div className="card-surface py-10 text-center text-navy/40 text-sm">Elige un cliente para ver su expediente 360° consolidado.</div>}
      {!loading && data && (
        <>
          <div className="card-surface p-5 flex items-center gap-5" data-testid="c360-health">
            <div className="text-center">
              <div className={`font-editorial text-5xl ${NIVEL_COLOR[data.health.nivel] || "text-navy"}`}>{data.health.score}</div>
              <div className="text-[11px] text-navy/40">Health Score</div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-lg"><HeartPulse className={`w-5 h-5 ${NIVEL_COLOR[data.health.nivel]}`} /> <span className="font-medium text-navy/80" data-testid="c360-nivel">{data.health.nivel}</span></div>
              <div className="text-sm text-navy/45 mt-1">Cliente: {data.cuenta}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="c360-kpis">
            {[["Oport. abiertas", data.kpis.oportunidades_abiertas], ["Pipeline", mxn(data.kpis.pipeline_abierto)], ["Ganado", mxn(data.kpis.valor_ganado)], ["Cotizaciones", data.kpis.cotizaciones], ["Actividades", data.kpis.actividades], ["Solic. abiertas", data.kpis.solicitudes_abiertas], ["Contactos", data.kpis.contactos]].map(([l, v]) => (
              <div key={l} className="card-surface p-3"><div className="text-xs text-navy/45">{l}</div><div className="font-editorial text-2xl text-navy/90">{v}</div></div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <Section title="Oportunidades" items={data.bundle.opportunities} render={row("opportunities")} />
            <Section title="Cotizaciones" items={data.bundle.quotes} render={row("quotes")} />
            <Section title="Actividades" items={data.bundle.activities} render={row("sales_activities")} />
            <Section title="Contactos" items={data.bundle.contacts} render={row("contacts")} />
            <Section title="Solicitudes" items={data.bundle.requests} render={row("customer_requests")} />
            <Section title="Feedback" items={data.bundle.feedback} render={row("customer_feedback")} />
          </div>
        </>
      )}
    </div>
  );
}
